import { createSelector, OutputSelector, Selector } from '@reduxjs/toolkit';
import { RootState } from 'Data/Redux';

type UnaryFunction<A, B> = (arg0: A) => B;

/**
 * Creates a new selector that pipes an existing selector through a series of transformational functions, in order
 *
 * @todo Create a properly memoised selector using re-reselect
 */
export default function pipe<A, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, C, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, C>,
  f3: UnaryFunction<C, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, C, D, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, C>,
  f3: UnaryFunction<C, D>,
  f4: UnaryFunction<D, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, C, D, E, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, C>,
  f3: UnaryFunction<C, D>,
  f4: UnaryFunction<D, E>,
  f5: UnaryFunction<E, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, C, D, E, F, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, C>,
  f3: UnaryFunction<C, D>,
  f4: UnaryFunction<D, E>,
  f5: UnaryFunction<E, F>,
  f6: UnaryFunction<F, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, C, D, E, F, G, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, C>,
  f3: UnaryFunction<C, D>,
  f4: UnaryFunction<D, E>,
  f5: UnaryFunction<E, F>,
  f6: UnaryFunction<F, G>,
  f7: UnaryFunction<G, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

export default function pipe<A, B, C, D, E, F, G, H, R>(
  selector: Selector<RootState, A>,
  f1: UnaryFunction<A, B>,
  f2: UnaryFunction<B, C>,
  f3: UnaryFunction<C, D>,
  f4: UnaryFunction<D, E>,
  f5: UnaryFunction<E, F>,
  f6: UnaryFunction<F, G>,
  f7: UnaryFunction<G, H>,
  f8: UnaryFunction<H, R>
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<A, R>>;

// Extend this pattern of typed overloaded methods as required

// Arbitrary number of functions (no typing)
export default function pipe<R>(
  selector: Selector<RootState, any>,
  ...functions: UnaryFunction<any, any>[]
): OutputSelector<ReadonlyArray<Selector<RootState>>, R, UnaryFunction<any, R>>;

export default function pipe(
  selector: Selector<RootState, any>,
  ...functions: UnaryFunction<any, any>[]
): OutputSelector<ReadonlyArray<Selector<RootState>>, any, UnaryFunction<any, any>> {
  return createSelector(selector, (v) => {
    // Wanted to use a library function, but most do not support arbitrary arguments.
    // redux.compose does...but it (or reversing the functions for it) messes with the selector
    //  and triggers a re-render when it hasn't changed if you pipe more than one function

    // TODO: Set up some tests to make sure piping doesn't cause selectors to recompute/change more than necessary.
    functions.forEach((f) => (v = f(v)));
    return v;
  });
}
